/*
 * STRICT vs LOOSE: how far through the funnel one visitor has got, and what that adds to each step's numbers. Pure (no DOM, no d3, no network). Chris's terms (2026-10-17):
 *
 *  - STRICT: a visitor must go STRAIGHT from each step to the next. The moment their next page is anything else, they have left the funnel (their path is "broken") and stay out of it.
 *  - LOOSE:  a visitor may wander. They only have to reach the steps IN ORDER; pages in between do not matter. A visitor who has wandered off but is still on the site is not lost:
 *            they can still reach the next step, so they are "still in play", not dropped off. Only a visit that has ENDED without reaching the next step is a drop off.
 *
 * Both are CUMULATIVE: a visitor only counts at a step if they entered at step 1 and got there by the rules above, so the funnel follows the same group of people from step to step and
 * each step can only be smaller than the one before. Both read only the FIRST visit to step 1 (a later return to step 1 starts nothing new), the same "first visit" rule as the drawer's
 * "Went to" list.
 *
 * `pages` is the funnel in order with the goal LAST. `k` is how many of them the visitor has reached: 0 = not in the funnel, 1 = reached step 1, ..., pages.length = reached the goal.
 */
export type FunnelMode = "strict" | "loose";

export interface Progress {
  k: number;
  /** strict only: the visitor's next page was not the next step, so the path is broken for good */
  broken: boolean;
}

export const START: Progress = { k: 0, broken: false };

/** The visitor has just landed on `page`: where are they in the funnel now? */
export const advance = (p: Progress, page: string, pages: string[], mode: FunnelMode): Progress => {
  if (!pages.length) return p;
  if (p.k === 0) return page === pages[0] ? { k: 1, broken: false } : p;
  if (p.broken || p.k >= pages.length) return p;                    // broken for good, or already at the goal: nothing more can change
  if (page === pages[p.k]) return { k: p.k + 1, broken: false };    // the next step, reached
  if (mode === "strict" && page !== pages[p.k - 1]) return { k: p.k, broken: true };   // anything else breaks a strict path (reloading the same page does not)

  return p;
};

/** Where a whole path (the pages a visitor saw, in order) has got them. */
export const progressOf = (path: string[], pages: string[], mode: FunnelMode): Progress => path.reduce((p, page) => advance(p, page, pages, mode), START);

/** One step's tally. `inPlay` = visitors still on the site who have neither gone on nor dropped off yet. */
export interface StepCount {
  visits: number;
  wentOn: number;
  elsewhere: number;
  left: number;
  inPlay: number;
}
export const emptyCounts = (steps: number): StepCount[] => Array.from({ length: steps }, () => ({ visits: 0, wentOn: 0, elsewhere: 0, left: 0, inPlay: 0 }));

/**
 * Adds one visitor's contribution to the steps' tallies (`counts` has one entry per STEP, not for the goal). `ended` = their visit is over. `weight` lets a sample stand for more people.
 * Every step the visitor reached counts as a visit; every step they got past counts as "went on"; the step they are at (their frontier) is where it depends:
 *   strict, path unbroken:  still on the site -> in play;   visit ended -> left the site
 *   strict, path broken:    went elsewhere (open or ended: the straight path is gone either way)
 *   loose:                  still on the site -> in play;   visit ended -> dropped off (counted as `left`)
 */
export const addVisitor = (counts: StepCount[], p: Progress, ended: boolean, mode: FunnelMode, weight = 1): void => {
  const steps = counts.length;
  for (let j = 1; j <= Math.min(p.k, steps); j++) {
    const c = counts[j - 1];
    c.visits += weight;
    if (j < p.k) c.wentOn += weight;
  }
  if (p.k >= 1 && p.k <= steps) {
    const c = counts[p.k - 1];
    if (mode === "strict" && p.broken) c.elsewhere += weight;
    else if (ended) c.left += weight;
    else c.inPlay += weight;
  }
};
